import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';

// Split into three contexts so consumers only re-render when their slice changes:
// - CartItemsContext  → components that need the full cart array
// - CartActionsContext → add/remove/update (stable refs via useCallback, never re-renders consumers)
// - CartCountContext  → Navbar badge (re-renders only when count changes, not on item edits)

export const CartItemsContext  = createContext([]);
export const CartActionsContext = createContext(null);
export const CartCountContext  = createContext(0);

// Legacy single context kept for backward-compat — points to the items context
export const CartContext = CartItemsContext;

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try { localStorage.setItem('cart', JSON.stringify(cart)); } catch {}
  }, [cart]);

  const addToCart = useCallback((product) => {
    setCart(prev => {
      if (product.isBundle) {
        const id = `bundle_${product._id}`;
        const exists = prev.find(i => i._id === id);
        if (exists) return prev.map(i => i._id === id ? { ...i, quantity: i.quantity + (product.quantity || 1) } : i);
        return [...prev, { ...product, _id: id, quantity: product.quantity || 1, isBundle: true, bundleProducts: product.products, price: product.bundlePrice, discountedPrice: product.bundlePrice }];
      }
      const exists = prev.find(i =>
        i._id === product._id &&
        i.selectedSize === product.selectedSize &&
        i.selectedColor === product.selectedColor &&
        i.selectedDesignIndex === product.selectedDesignIndex
      );
      if (exists) {
        return prev.map(i =>
          (i._id === product._id && i.selectedSize === product.selectedSize && i.selectedColor === product.selectedColor && i.selectedDesignIndex === product.selectedDesignIndex)
            ? { ...i, quantity: i.quantity + (product.quantity || 1) }
            : i
        );
      }
      return [...prev, { ...product, quantity: product.quantity || 1 }];
    });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart(prev => prev.filter(i => i._id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, newQuantity) => {
    if (newQuantity < 1) { removeFromCart(productId); return; }
    setCart(prev => prev.map(i => i._id === productId ? { ...i, quantity: newQuantity } : i));
  }, [removeFromCart]);

  const clearCart = useCallback(() => setCart([]), []);

  // Stable actions object — never changes reference, so CartActionsContext consumers never re-render
  const actions = useMemo(() => ({ addToCart, removeFromCart, updateQuantity, clearCart }), [addToCart, removeFromCart, updateQuantity, clearCart]);

  const cartCount = useMemo(() => cart.reduce((n, i) => n + i.quantity, 0), [cart]);

  const cartTotal = useMemo(() => cart.reduce((t, i) => t + ((i.discountedPrice || i.originalPrice || i.price) * i.quantity), 0), [cart]);

  // Merge total into items context value for components that need both
  const itemsValue = useMemo(() => ({ cart, cartTotal }), [cart, cartTotal]);

  return (
    <CartActionsContext.Provider value={actions}>
      <CartCountContext.Provider value={cartCount}>
        <CartItemsContext.Provider value={itemsValue}>
          {children}
        </CartItemsContext.Provider>
      </CartCountContext.Provider>
    </CartActionsContext.Provider>
  );
};

// Hooks
export const useCart = () => {
  const { cart, cartTotal } = useContext(CartItemsContext);
  const actions = useContext(CartActionsContext);
  const cartCount = useContext(CartCountContext);
  if (!actions) throw new Error('useCart must be used within CartProvider');
  return { cart, cartTotal, cartCount, ...actions };
};

export const useCartCount = () => useContext(CartCountContext);
export const useCartActions = () => useContext(CartActionsContext);
